

export const dynamic = "force-dynamic";

import { useState, useEffect, useMemo } from "react";
import { 
  Search, 
  Calendar,
  Building2,
  Download,
  FileText,
  Image as ImageIcon,
} from "lucide-react";
// @ts-ignore - react-csv doesn't have types
import { CSVLink } from "react-csv";

// INR Icon Component
const INRIcon = ({ className }: { className?: string }) => (
  <span className={className}>₹</span>
);

import DashboardLayout from "../components/DashboardLayout";
import Modal from "../components/Modal";
import DateRangePicker from "../components/DateRangePicker";
import SearchableDropdown from "../components/SearchableDropdown";
import { ReportTable } from "../components/ReportTable";
// @ts-ignore - mastersApi may be used in future
import { mastersApi, vendorsApi, apiErrorMessage } from "@/lib/api";
import { getImageUrl, byNewest } from "@/lib/utils";
import { useToast } from "@/context/ToastContext";
import {formatCurrencyFull, formatDate } from "@/lib/data";

// Types
interface VendorPayment {
  id: number;
  vendor_id: number;
  event_vendor_id?: number;
  expense_id?: number;
  vendor?: { 
    id: number; 
    name: string; 
    type: string;
    contact_person?: string;
    phone?: string;
    email?: string;
  };
  amount: number;
  paid_at: string;
  payment_method: string;
  reference?: string;
  notes?: string;
  proof_path?: string;
  event_vendor?: {
    id: number;
    vendor_id: number;
    event?: { 
      id: number;
      name: string;
      start_date?: string;
      client_name?: string;
    };
  };
  expense?: {
    id: number;
    title: string;
    description?: string;
    category?: string;
    expense_date?: string;
    paid_to?: string;
    proof_path?: string;
    event?: {
      id: number;
      name: string;
      start_date?: string;
      client_name?: string;
    } | null;
  } | null;
  created_at?: string;
}

interface Vendor {
  id: number;
  name: string;
  type?: string;
  vendor_type_id?: number | null;
}

// Action Button Component
function ActionButton({ 
  onClick, 
  icon: Icon, 
  label, 
  variant = 'default' 
}: { 
  onClick: () => void; 
  icon: any; 
  label: string;
  variant?: 'default' | 'primary' | 'danger';
}) {
  const colors = {
    default: 'text-stone-600 hover:bg-stone-100',
    primary: 'text-amber-600 hover:bg-amber-50',
    danger: 'text-red-600 hover:bg-red-50',
  };

  return (
    <button
      onClick={onClick}
      className={`flex items-center gap-1.5 px-2 py-1.5 rounded-lg text-xs font-medium transition-colors ${colors[variant]}`}
      title={label}
    >
      <Icon className="w-4 h-4" />
      <span className="hidden sm:inline">{label}</span>
    </button>
  );
}

// Payment Form Component
function PaymentForm({ 
  initialData, 
  onSubmit, 
  onCancel,
  vendors
}: { 
  initialData?: Partial<VendorPayment>; 
  onSubmit: (data: any) => void;
  onCancel: () => void;
  vendors: Vendor[];
}) {
  const [formData, setFormData] = useState<{
    vendor_id: string;
    amount: string;
    payment_date: string;
    payment_method: string;
    reference: string;
    notes: string;
  }>({
    vendor_id: initialData?.vendor_id ? String(initialData.vendor_id) : "",
    amount: initialData?.amount ? String(initialData.amount) : "",
    payment_date: initialData?.paid_at ? new Date(initialData.paid_at).toISOString().slice(0, 10) : new Date().toISOString().slice(0, 10),
    payment_method: initialData?.payment_method || "cash",
    reference: initialData?.reference || "",
    notes: initialData?.notes || "",
  });

  const [paymentMethodOptions, setPaymentMethodOptions] = useState([
    { value: "cash", label: "Cash" },
    { value: "bank_transfer", label: "Bank Transfer" },
    { value: "cheque", label: "Cheque" },
    { value: "upi", label: "UPI" },
    { value: "card", label: "Card" },
  ]);

  useEffect(() => {
    mastersApi.paymentMethods.list({ status: 'active' })
      .then((res) => {
        const data = res.data?.data || res.data || [];
        if (data.length > 0) {
          setPaymentMethodOptions(data.map((m: any) => ({ value: m.name, label: m.name })));
        }
      })
      .catch(() => {});
  }, []);

  // Add useEffect for form reset when initialData changes
  useEffect(() => {
    if (!initialData?.id) {
      // Reset to defaults for new payment
      setFormData({
        vendor_id: "",
        amount: "",
        payment_date: new Date().toISOString().slice(0, 10),
        payment_method: "cash",
        reference: "",
        notes: "",
      });
    } else {
      // Populate with existing data
      setFormData({
        vendor_id: String(initialData.vendor_id || ""),
        amount: String(initialData.amount || ""),
        payment_date: initialData.paid_at ? new Date(initialData.paid_at).toISOString().slice(0, 10) : new Date().toISOString().slice(0, 10),
        payment_method: initialData.payment_method || "cash",
        reference: initialData.reference || "",
        notes: initialData.notes || "",
      });
    }
  }, [initialData]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit({
      vendor_id: parseInt(formData.vendor_id),
      amount: parseFloat(formData.amount),
      payment_date: formData.payment_date,
      payment_method: formData.payment_method,
      transaction_id: formData.reference,
      notes: formData.notes,
    });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label className="block text-xs font-medium text-stone-500 mb-1.5">Vendor *</label>
        <div className="relative">
          <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
          <SearchableDropdown
            options={[
              { value: "", label: "Select a vendor" },
              ...vendors.map((vendor) => ({
                value: String(vendor.id),
                label: vendor.name,
              })),
            ]}
            value={formData.vendor_id}
            onChange={(value) => setFormData({ ...formData, vendor_id: value })}
            placeholder="Select a vendor"
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-xs font-medium text-stone-500 mb-1.5">Amount *</label>
          <div className="relative">
          <INRIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400 font-bold" />
            <input
              type="number"
              step="0.01"
              value={formData.amount}
              onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
              placeholder="0.00"
              className="w-full pl-9 pr-3 py-2.5 rounded-xl border border-stone-200 text-sm focus:border-amber-400 focus:ring-2 focus:ring-amber-100 outline-none"
              required
            />
          </div>
        </div>
        <div>
          <label className="block text-xs font-medium text-stone-500 mb-1.5">Payment Date *</label>
          <div className="relative">
            <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
            <input
              type="date"
              value={formData.payment_date}
              onChange={(e) => setFormData({ ...formData, payment_date: e.target.value })}
              className="w-full pl-9 pr-3 py-2.5 rounded-xl border border-stone-200 text-sm focus:border-amber-400 focus:ring-2 focus:ring-amber-100 outline-none"
              required
            />
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-xs font-medium text-stone-500 mb-1.5">Payment Method</label>
          <SearchableDropdown
            options={paymentMethodOptions}
            value={formData.payment_method}
            onChange={(value) => setFormData({ ...formData, payment_method: value })}
            placeholder="Select payment method"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-stone-500 mb-1.5">Reference Number</label>
          <input
            type="text"
            value={formData.reference}
            onChange={(e) => setFormData({ ...formData, reference: e.target.value })}
            placeholder="Transaction ID / Cheque No"
            className="w-full px-3 py-2.5 rounded-xl border border-stone-200 text-sm focus:border-amber-400 focus:ring-2 focus:ring-amber-100 outline-none"
          />
        </div>
      </div>

      <div>
        <label className="block text-xs font-medium text-stone-500 mb-1.5">Notes</label>
        <textarea
          value={formData.notes}
          onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
          placeholder="Additional notes..."
          rows={2}
          className="w-full px-3 py-2.5 rounded-xl border border-stone-200 text-sm focus:border-amber-400 focus:ring-2 focus:ring-amber-100 outline-none resize-none"
        />
      </div>

      <div className="flex gap-3 pt-2">
        <button
          type="button"
          onClick={onCancel}
          className="flex-1 px-4 py-2.5 rounded-xl bg-stone-100 text-stone-600 font-semibold text-sm hover:bg-stone-200 transition-colors border border-stone-200"
        >
          Cancel
        </button>
        <button
          type="submit"
          className="flex-1 px-4 py-2.5 rounded-xl text-white font-semibold text-sm gold-gradient hover:opacity-90 transition-opacity"
        >
          {initialData ? "Update" : "Add"} Payment
        </button>
      </div>
    </form>
  );
}

// Payment Detail Modal Component
function PaymentDetailModal({ 
  payment, 
  isOpen, 
  onClose 
}: { 
  payment: VendorPayment | null;
  isOpen: boolean;
  onClose: () => void;
}) {
  if (!payment) return null;

  const proofUrl = payment.proof_path ? getImageUrl(payment.proof_path) : null;
  const expenseProofUrl = payment.expense?.proof_path ? getImageUrl(payment.expense.proof_path) : null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Payment Details"
      size="lg"
    >
      <div className="space-y-6">
        {/* Payment Information */}
        <div className="bg-stone-50 rounded-xl p-4">
          <h3 className="text-sm font-semibold text-stone-800 mb-3 flex items-center gap-2">
            <FileText className="w-4 h-4 text-amber-600" />
            Payment Information
          </h3>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-stone-500">Amount:</span>
              <p className="font-semibold text-emerald-600 text-lg">{formatCurrencyFull(payment.amount)}</p>
            </div>
            <div>
              <span className="text-stone-500">Payment Date:</span>
              <p className="font-medium">{formatDate(payment.paid_at)}</p>
            </div>
            <div>
              <span className="text-stone-500">Payment Method:</span>
              <p className="font-medium capitalize">{payment.payment_method}</p>
            </div>
            <div>
              <span className="text-stone-500">Reference:</span>
              <p className="font-medium">{payment.reference || 'N/A'}</p>
            </div>
            {payment.notes && (
              <div className="col-span-2">
                <span className="text-stone-500">Notes:</span>
                <p className="font-medium mt-1">{payment.notes}</p>
              </div>
            )}
          </div>
        </div>

        {/* Vendor Information */}
        <div className="bg-stone-50 rounded-xl p-4">
          <h3 className="text-sm font-semibold text-stone-800 mb-3 flex items-center gap-2">
            <Building2 className="w-4 h-4 text-amber-600" />
            Vendor Information
          </h3>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-stone-500">Vendor Name:</span>
              <p className="font-medium">{payment.vendor?.name || 'Unknown'}</p>
            </div>
            <div>
              <span className="text-stone-500">Vendor Type:</span>
              <p className="font-medium">{payment.vendor?.type || 'N/A'}</p>
            </div>
            {payment.vendor?.contact_person && (
              <div>
                <span className="text-stone-500">Contact Person:</span>
                <p className="font-medium">{payment.vendor.contact_person}</p>
              </div>
            )}
            {payment.vendor?.phone && (
              <div>
                <span className="text-stone-500">Phone:</span>
                <p className="font-medium">{payment.vendor.phone}</p>
              </div>
            )}
            {payment.vendor?.email && (
              <div className="col-span-2">
                <span className="text-stone-500">Email:</span>
                <p className="font-medium">{payment.vendor.email}</p>
              </div>
            )}
          </div>
        </div>

        {/* Event Information */}
        <div className="bg-stone-50 rounded-xl p-4">
          <h3 className="text-sm font-semibold text-stone-800 mb-3 flex items-center gap-2">
            <Calendar className="w-4 h-4 text-amber-600" />
            Event Information
          </h3>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-stone-500">Event Name:</span>
              <p className="font-medium">{payment.event_vendor?.event?.name || 'N/A'}</p>
            </div>
            <div>
              <span className="text-stone-500">Event Date:</span>
              <p className="font-medium">{payment.event_vendor?.event?.start_date ? formatDate(payment.event_vendor.event.start_date) : 'N/A'}</p>
            </div>
            {payment.event_vendor?.event?.client_name && (
              <div className="col-span-2">
                <span className="text-stone-500">Client:</span>
                <p className="font-medium">{payment.event_vendor.event.client_name}</p>
              </div>
            )}
          </div>
        </div>

        {/* Expense Information (if exists) */}
        {payment.expense && (
          <div className="bg-stone-50 rounded-xl p-4">
            <h3 className="text-sm font-semibold text-stone-800 mb-3 flex items-center gap-2">
              <FileText className="w-4 h-4 text-amber-600" />
              Expense Details
            </h3>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-stone-500">Expense Title:</span>
                <p className="font-medium">{payment.expense.title}</p>
              </div>
              <div>
                <span className="text-stone-500">Category:</span>
                <p className="font-medium">{payment.expense.category || 'N/A'}</p>
              </div>
              <div>
                <span className="text-stone-500">Expense Date:</span>
                <p className="font-medium">{payment.expense.expense_date ? formatDate(payment.expense.expense_date) : 'N/A'}</p>
              </div>
              <div>
                <span className="text-stone-500">Paid To:</span>
                <p className="font-medium">{payment.expense.paid_to || 'N/A'}</p>
              </div>
              {payment.expense.event && (
                <div className="col-span-2 bg-white rounded-lg p-3 border border-stone-200">
                  <span className="text-xs text-stone-500 block mb-2">Linked Event Details:</span>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <span className="text-stone-400 text-xs">Event Name:</span>
                      <p className="font-medium text-stone-800">{payment.expense.event.name}</p>
                    </div>
                    <div>
                      <span className="text-stone-400 text-xs">Event Date:</span>
                      <p className="font-medium text-stone-800">{payment.expense.event.start_date ? formatDate(payment.expense.event.start_date) : 'N/A'}</p>
                    </div>
                    {payment.expense.event.client_name && (
                      <div className="col-span-2">
                        <span className="text-stone-400 text-xs">Customer Name:</span>
                        <p className="font-medium text-stone-800">{payment.expense.event.client_name}</p>
                      </div>
                    )}
                  </div>
                </div>
              )}
              {payment.expense.description && (
                <div className="col-span-2">
                  <span className="text-stone-500">Description:</span>
                  <p className="font-medium mt-1">{payment.expense.description}</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Proof Documents */}
        {(proofUrl || expenseProofUrl) && (
          <div className="bg-stone-50 rounded-xl p-4">
            <h3 className="text-sm font-semibold text-stone-800 mb-3 flex items-center gap-2">
              <ImageIcon className="w-4 h-4 text-amber-600" />
              Proof Documents
            </h3>
            <div className="space-y-4">
              {proofUrl && (
                <div>
                  <p className="text-xs text-stone-500 mb-2">Payment Proof:</p>
                  <a 
                    href={proofUrl} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="block rounded-lg overflow-hidden border-2 border-stone-200 hover:border-amber-400 transition-colors"
                  >
                    <img 
                      src={proofUrl} 
                      alt="Payment Proof" 
                      className="w-full h-auto max-h-64 object-contain bg-white"
                    />
                  </a>
                </div>
              )}
              {expenseProofUrl && (
                <div>
                  <p className="text-xs text-stone-500 mb-2">Expense Proof:</p>
                  <a 
                    href={expenseProofUrl} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="block rounded-lg overflow-hidden border-2 border-stone-200 hover:border-amber-400 transition-colors"
                  >
                    <img 
                      src={expenseProofUrl} 
                      alt="Expense Proof" 
                      className="w-full h-auto max-h-64 object-contain bg-white"
                    />
                  </a>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Close Button */}
        <div className="flex justify-end pt-2">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-xl bg-stone-100 text-stone-600 font-semibold text-sm hover:bg-stone-200 transition-colors border border-stone-200"
          >
            Close
          </button>
        </div>
      </div>
    </Modal>
  );
}

// Main Page Component
export default function VendorPaymentsPage() {
  const { showToast } = useToast();
  
  const [payments, setPayments] = useState<VendorPayment[]>([]);
  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [vendorFilter, setVendorFilter] = useState<string>('all');
  const [paymentMethodFilter, setPaymentMethodFilter] = useState<string>('all');
  const [vendorTypeFilter, setVendorTypeFilter] = useState<string>('all');
  const [vendorTypes, setVendorTypes] = useState<{id: number; name: string}[]>([]);
  const [paymentMethods, setPaymentMethods] = useState<string[]>([]);
  
  const [viewingPayment, setViewingPayment] = useState<VendorPayment | null>(null);
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');

  useEffect(() => {
    loadData();
    loadFilters();
  }, []);

  const loadFilters = async () => {
    try {
      // Load vendor types
      const vtRes = await mastersApi.vendorTypes.list();
      const vtData = vtRes.data?.data || vtRes.data || [];
      setVendorTypes(Array.isArray(vtData) ? vtData : []);
      
      // Load payment methods
      const pmRes = await mastersApi.paymentMethods.list({ status: 'active' });
      const pmData = pmRes.data?.data || pmRes.data || [];
      if (Array.isArray(pmData)) {
        setPaymentMethods(pmData.map((m: any) => m.name));
      }
    } catch (error) {
      console.error("Failed to load filters:", error);
    }
  };

  const loadData = async () => {
    setLoadError('');
    try {
      setLoading(true);
      
      // Fetch vendors list first
      const vendorsRes = await vendorsApi.list('for_select=1');
      const vendorsData = vendorsRes.data?.data || vendorsRes.data || [];
      const vendorsArray = Array.isArray(vendorsData) ? vendorsData : [];
      setVendors(vendorsArray);
      
      // Create vendor lookup map for quick access
      const vendorMap = new Map(vendorsArray.map((v: any) => [v.id, v]));
      
      // Fetch vendor payments using the new API
      let paymentsData: any[] = [];
      try {
        const paymentsRes = await mastersApi.vendorPayments.list({ per_page: 1000 });
        if (paymentsRes.data?.data?.data) {
          // Paginated response
          paymentsData = paymentsRes.data.data.data;
        } else if (paymentsRes.data?.data) {
          paymentsData = paymentsRes.data.data;
        } else if (Array.isArray(paymentsRes.data)) {
          paymentsData = paymentsRes.data;
        }
      } catch (apiError) {
        // Say so, instead of showing "No payments found"
        setLoadError(apiErrorMessage(apiError, "Couldn't load vendor payments"));
      }
      
      // Process payments to extract vendor info from relationships
      const processedPayments = (Array.isArray(paymentsData) ? paymentsData : []).map((payment: any) => {
        // Extract vendor from nested relationship
        const vendor = payment.vendor || 
                      payment.event_vendor?.vendor || 
                      { id: payment.vendor_id, name: `Vendor #${payment.vendor_id}` };
        
        return {
          id: payment.id,
          vendor_id: payment.vendor_id || payment.event_vendor?.vendor_id,
          event_vendor_id: payment.event_vendor_id,
          expense_id: payment.expense_id,
          vendor: {
            id: vendor.id,
            name: vendor.name,
            type: vendor.type || '',
            contact_person: vendor.contact_person,
            phone: vendor.phone,
            email: vendor.email,
          },
          amount: parseFloat(payment.amount || 0),
          paid_at: payment.paid_at || payment.payment_date,
          payment_method: payment.payment_method || 'Cash',
          reference: payment.reference || payment.transaction_id || '',
          notes: payment.notes || '',
          proof_path: payment.proof_path,
          event_vendor: payment.event_vendor || null,
          expense: payment.expense || null,
          created_at: payment.created_at,
        };
      });
      
      setPayments(processedPayments);
      
    } catch (error: any) {
      showToast("Failed to load payments", "error");
      console.error("Error loading vendor payments:", error);
    } finally {
      setLoading(false);
    }
  };

  const filteredPayments = useMemo(() => {
    let filtered = Array.isArray(payments) ? payments : [];
    
    // Date range filter
    if (fromDate && toDate) {
      filtered = filtered.filter(p => {
        const paymentDate = new Date(p.paid_at || Date.now());
        const from = new Date(fromDate);
        from.setHours(0, 0, 0, 0); // include the whole start day (was UTC-midnight, dropping early payments)
        const to = new Date(toDate);
        to.setHours(23, 59, 59, 999);
        return paymentDate >= from && paymentDate <= to;
      });
    }
    
    // Search query filter
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      filtered = filtered.filter(p => 
        (p.vendor?.name || '').toLowerCase().includes(q) ||
        (p.reference || '').toLowerCase().includes(q) ||
        (p.notes || '').toLowerCase().includes(q) ||
        (p.event_vendor?.event?.name || '').toLowerCase().includes(q)
      );
    }
    
    // Vendor filter
    if (vendorFilter !== 'all') {
      filtered = filtered.filter(p => p.vendor_id === parseInt(vendorFilter));
    }
    
    // Payment method filter
    if (paymentMethodFilter !== 'all') {
      filtered = filtered.filter(p => p.payment_method === paymentMethodFilter);
    }
    
    // Vendor type filter
    if (vendorTypeFilter !== 'all') {
      filtered = filtered.filter(p => {
        const vendor = vendors.find(v => v.id === p.vendor_id);
        return !!vendor && String(vendor.vendor_type_id) === vendorTypeFilter;
      });
    }

    // Latest recorded payments first
    return [...filtered].sort(byNewest);
  }, [payments, searchQuery, vendorFilter, paymentMethodFilter, vendorTypeFilter, fromDate, toDate, vendors]);

  const openDeleteModal = (payment: VendorPayment) => {
    // Not used in reports page - view only
  };

  const handleDelete = async () => {
    // Not used in reports page - view only
  };

  const csvData = useMemo(() => {
    return filteredPayments.map(payment => ({
      'Payment ID': payment.id,
      'Vendor Name': payment.vendor?.name || 'Unknown',
      'Vendor Type': payment.vendor?.type || '',
      'Contact Person': payment.vendor?.contact_person || '',
      'Phone': payment.vendor?.phone || '',
      'Email': payment.vendor?.email || '',
      'Event Name': payment.event_vendor?.event?.name || 'N/A',
      'Event Date': payment.event_vendor?.event?.start_date || '',
      'Client Name': payment.event_vendor?.event?.client_name || '',
      'Amount': payment.amount,
      'Payment Date': formatDate(payment.paid_at),
      'Payment Method': payment.payment_method,
      'Reference': payment.reference || '',
      'Expense Title': payment.expense?.title || '',
      'Expense Category': payment.expense?.category || '',
      'Expense Event': payment.expense?.event?.name || '',
      'Expense Event Date': payment.expense?.event?.start_date || '',
      'Expense Customer': payment.expense?.event?.client_name || '',
      'Notes': payment.notes || '',
    }));
  }, [filteredPayments]);

  const csvHeaders = [
    { label: 'Payment ID', key: 'Payment ID' },
    { label: 'Vendor Name', key: 'Vendor Name' },
    { label: 'Vendor Type', key: 'Vendor Type' },
    { label: 'Contact Person', key: 'Contact Person' },
    { label: 'Phone', key: 'Phone' },
    { label: 'Email', key: 'Email' },
    { label: 'Event Name', key: 'Event Name' },
    { label: 'Event Date', key: 'Event Date' },
    { label: 'Client Name', key: 'Client Name' },
    { label: 'Amount', key: 'Amount' },
    { label: 'Payment Date', key: 'Payment Date' },
    { label: 'Payment Method', key: 'Payment Method' },
    { label: 'Reference', key: 'Reference' },
    { label: 'Expense Title', key: 'Expense Title' },
    { label: 'Expense Category', key: 'Expense Category' },
    { label: 'Expense Event', key: 'Expense Event' },
    { label: 'Expense Event Date', key: 'Expense Event Date' },
    { label: 'Expense Customer', key: 'Expense Customer' },
    { label: 'Notes', key: 'Notes' },
  ];

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-2 border-amber-500 border-t-transparent"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-stone-800 mb-1">Vendor Payments</h1>
        <p className="text-sm text-stone-500">Track and manage payments to vendors</p>
      </div>

      {loadError && (
        <div className="mb-4 flex flex-wrap items-center justify-between gap-2 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-800" role="alert">
          <span>{loadError}</span>
          <button type="button" onClick={() => { setLoading(true); loadData(); }} className="h-10 px-3 rounded-lg bg-white border border-rose-200 font-medium hover:bg-rose-100">
            Try again
          </button>
        </div>
      )}

      {/* Toolbar */}
      <div className="flex flex-col lg:flex-row gap-3 mb-6">
        <div className="flex-1">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search payments..."
              aria-label="Search payments"
              className="w-full pl-9 pr-4 py-2.5 lg:py-2 rounded-xl border border-stone-200 text-sm focus:border-amber-400 focus:ring-2 focus:ring-amber-100 outline-none"
            />
          </div>
        </div>
        {/* Filters: stacked full-width on mobile, 2-up on small screens, inline row on large screens */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 lg:flex lg:flex-wrap lg:items-center">
          <DateRangePicker
            fromDate={fromDate}
            toDate={toDate}
            onFromChange={setFromDate}
            onToChange={setToDate}
          />
          <SearchableDropdown
            options={[
              { value: "all", label: "All Vendors" },
              ...vendors.map((vendor) => ({
                value: String(vendor.id),
                label: vendor.name,
              })),
            ]}
            value={vendorFilter}
            onChange={setVendorFilter}
            placeholder="Filter by vendor"
          />
          <SearchableDropdown
            options={[
              { value: "all", label: "All Types" },
              ...vendorTypes.map((vt) => ({
                value: String(vt.id),
                label: vt.name,
              })),
            ]}
            value={vendorTypeFilter}
            onChange={setVendorTypeFilter}
            placeholder="Filter by type"
          />
          <SearchableDropdown
            options={[
              { value: "all", label: "All Methods" },
              ...paymentMethods.map((pm) => ({
                value: pm,
                label: pm,
              })),
            ]}
            value={paymentMethodFilter}
            onChange={setPaymentMethodFilter}
            placeholder="Payment method"
          />
          <CSVLink
            data={csvData}
            headers={csvHeaders}
            filename={`vendor-payments-${new Date().toISOString().split('T')[0]}.csv`}
            className="px-4 py-2.5 lg:py-2 rounded-xl bg-emerald-600 text-white font-semibold text-sm hover:bg-emerald-700 transition-colors flex items-center justify-center gap-2"
          >
            <Download className="w-4 h-4" />
            Export CSV
          </CSVLink>
        </div>
      </div>

      {/* Content */}
      {filteredPayments.length === 0 ? (
        <div className="text-center py-12 bg-stone-50 rounded-xl border border-stone-200">
          <Building2 className="w-12 h-12 text-stone-300 mx-auto mb-3" />
          <p className="text-stone-500">No payments found</p>
        </div>
      ) : (
        <>
        {/* Mobile Cards */}
        <div className="md:hidden bg-white rounded-xl border border-stone-200 overflow-hidden divide-y divide-stone-100">
          {filteredPayments.map((payment) => {
            const eventName = payment.event_vendor?.event?.name || payment.expense?.event?.name || '-';
            return (
              <div
                key={payment.id}
                className="p-4 cursor-pointer active:bg-stone-50"
                onClick={() => setViewingPayment(payment)}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="font-semibold text-stone-800 truncate">{payment.vendor?.name || 'Unknown Vendor'}</p>
                    <p className="text-xs text-stone-500 mt-0.5 truncate">
                      {eventName} · {formatDate(payment.paid_at || new Date().toISOString())}
                    </p>
                  </div>
                  <div className="shrink-0 font-bold text-emerald-600 text-sm">{formatCurrencyFull(payment.amount)}</div>
                </div>
                <div className="mt-3 grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
                  <div className="min-w-0">
                    <p className="text-[11px] text-stone-400">Method</p>
                    <p className="text-stone-700 capitalize truncate">{payment.payment_method || 'Cash'}</p>
                  </div>
                  <div className="min-w-0">
                    <p className="text-[11px] text-stone-400">Reference</p>
                    <p className="text-stone-700 truncate">{payment.reference || '-'}</p>
                  </div>
                  {payment.vendor?.type && (
                    <div className="min-w-0">
                      <p className="text-[11px] text-stone-400">Vendor Type</p>
                      <p className="text-stone-700 truncate">{payment.vendor.type}</p>
                    </div>
                  )}
                </div>
                <div className="mt-3 flex flex-wrap items-center justify-end gap-2" onClick={(e) => e.stopPropagation()}>
                  <button
                    onClick={() => setViewingPayment(payment)}
                    className="h-10 min-w-10 px-3 inline-flex items-center justify-center gap-1.5 rounded-lg border border-stone-200 text-xs font-medium text-amber-600 hover:bg-amber-50 transition-colors"
                    aria-label={`View payment details for ${payment.vendor?.name || 'vendor'}`}
                  >
                    <FileText className="w-4 h-4" />
                    Details
                  </button>
                </div>
              </div>
            );
          })}
        </div>

        {/* Desktop Table */}
        <div className="hidden md:block">
        <ReportTable
          columns={[
            { key: 'vendor', label: 'Vendor', width: '20%' },
            { key: 'event', label: 'Event', width: '20%' },
            { key: 'date', label: 'Date', width: '12%' },
            { key: 'method', label: 'Method', width: '12%' },
            { key: 'reference', label: 'Reference', width: '16%' },
            { key: 'amount', label: 'Amount', align: 'right', width: '20%' },
          ]}
          data={filteredPayments.map(payment => {
            // Try to get event from multiple sources
            const eventName = payment.event_vendor?.event?.name || 
                             payment.expense?.event?.name || 
                             '-';
            
            return {
              id: payment.id,
              vendor: (
                <div>
                  <span className="font-medium text-stone-800">{payment.vendor?.name || 'Unknown Vendor'}</span>
                  {payment.vendor?.type && (
                    <p className="text-xs text-stone-400">{payment.vendor.type}</p>
                  )}
                </div>
              ),
              event: eventName,
              date: formatDate(payment.paid_at || new Date().toISOString()),
              method: <span className="capitalize">{payment.payment_method || 'Cash'}</span>,
              reference: payment.reference || '-',
              amount: payment.amount,
            };
          })}
          totalField="amount"
          onRowClick={(payment) => setViewingPayment(filteredPayments.find(p => p.id === payment.id) || null)}
          formatValue={(value, key) => {
            if (key === 'amount') {
              return <span className="font-bold text-emerald-600">{formatCurrencyFull(value)}</span>;
            }
            return value;
          }}
        />
        </div>
        </>
      )}

      {/* Total Summary */}
      {filteredPayments.length > 0 && (
        <div className="mt-4 bg-gradient-to-r from-amber-50 to-amber-100 rounded-xl p-4 border border-amber-200">
          <div className="flex items-center justify-between gap-3">
            <div className="min-w-0">
              <p className="text-sm font-semibold text-amber-800">Total Payments</p>
              <p className="text-xs text-amber-600">{filteredPayments.length} payment(s)</p>
            </div>
            <div className="text-right min-w-0">
              <p className="text-xl sm:text-2xl font-bold text-amber-700 break-words">
                {formatCurrencyFull(filteredPayments.reduce((sum, p) => sum + p.amount, 0))}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Payment Detail Modal */}
      <PaymentDetailModal
        payment={viewingPayment}
        isOpen={!!viewingPayment}
        onClose={() => setViewingPayment(null)}
      />
    </DashboardLayout>
  );
}
